/**
 * Types pour les services payants (optionnellement) d'un établissement.
 * Stockés en colonne JSONB `paid_services` sur la table establishments.
 *
 * Chaque service peut être activé avec un prix optionnel.
 * Si price est null, le service est affiché "sur demande" sur la fiche publique.
 */

export type PaidServicePriceType = 'per_day' | 'per_stay' | 'per_person' | 'per_kwh'

export interface PaidServiceEntry {
  enabled: boolean
  price?: number | null
  priceType?: PaidServicePriceType | null
}

export const PAID_SERVICE_KEYS = [
  'borne_recharge_voiture',
  'location_velos',
  'location_velos_electriques',
  'location_canoe_kayak',
  'location_raquettes_neige',
  'location_materiel_peche',
  'location_equipement_rando',
  'sauna',
  'bain_nordique',
  'hammam',
  'spa_jacuzzi',
  'soins_bien_etre',
] as const

export type PaidServiceKey = (typeof PAID_SERVICE_KEYS)[number]

export type PaidServices = Partial<Record<PaidServiceKey, PaidServiceEntry>>

export interface PaidServiceConfig {
  key: PaidServiceKey
  label: string
  icon: string
  description?: string
  priceOptional?: boolean
  allowPerKwh?: boolean
}

export const PAID_SERVICES_CONFIG: PaidServiceConfig[] = [
  {
    key: 'borne_recharge_voiture',
    label: 'Borne recharge véhicule électrique',
    icon: 'i-lucide-zap',
    allowPerKwh: true,
  },
  {
    key: 'location_velos',
    label: 'Location vélos',
    icon: 'i-lucide-bike',
  },
  {
    key: 'location_velos_electriques',
    label: 'Location vélos électriques',
    icon: 'i-lucide-zap',
  },
  {
    key: 'location_canoe_kayak',
    label: 'Location canoë / kayak / paddle',
    icon: 'i-lucide-waves',
  },
  {
    key: 'location_raquettes_neige',
    label: 'Location raquettes à neige',
    icon: 'i-lucide-mountain-snow',
  },
  {
    key: 'location_materiel_peche',
    label: 'Location matériel de pêche',
    icon: 'i-lucide-fish',
  },
  {
    key: 'location_equipement_rando',
    label: 'Location équipement randonnée',
    icon: 'i-lucide-footprints',
  },
  {
    key: 'sauna',
    label: 'Sauna',
    icon: 'i-lucide-flame',
  },
  {
    key: 'bain_nordique',
    label: 'Bain nordique / hot tub',
    icon: 'i-lucide-droplets',
  },
  {
    key: 'hammam',
    label: 'Hammam',
    icon: 'i-lucide-wind',
  },
  {
    key: 'spa_jacuzzi',
    label: 'Spa / jacuzzi',
    icon: 'i-lucide-sparkles',
  },
  {
    key: 'soins_bien_etre',
    label: 'Soins & bien-être sur place',
    icon: 'i-lucide-heart-pulse',
    description: 'Massage, soins, yoga… Laissez le tarif vide si variable selon la formule.',
    priceOptional: true,
  },
]
